import axios from 'axios';

// Environment-based URL selection
const ENV = process.env.REACT_APP_ENV || 'local';
const ENV_URLS = {
  local: process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3001',
  development: process.env.REACT_APP_DEV_API_URL || 'https://betagate.kubercrypto.io',
  staging: process.env.REACT_APP_STAGING_API_URL || 'https://betagate.kubercrypto.io',
  production: process.env.REACT_APP_PROD_API_URL || 'https://apigate.kubercrypto.io',
};
const BASE_URL = ENV_URLS[ENV] || ENV_URLS.local;

// Log environment on init (only in debug mode)
if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
  console.log(`[ApiClient] ENV: ${ENV}, API: ${BASE_URL}`);
}

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for browser session cookies (cross-origin)
  timeout: 30000,
});

// Store for API key (will be set by AuthContext)
let apiKey = null;

/**
 * Set API key for authenticated requests
 * @param {string} key - The API key to use
 */
export const setApiKey = (key) => {
  apiKey = key;
};

/**
 * Clear API key
 */
export const clearApiKey = () => {
  apiKey = null;
};

/**
 * Get current API key
 * @returns {string|null} The current API key
 */
export const getApiKey = () => apiKey;

// Request interceptor - add API key header for v1 endpoints
apiClient.interceptors.request.use(
  (config) => {
    // Add API key for v1 endpoints (orders, merchants)
    if (apiKey && config.url?.includes('/api/v1/')) {
      config.headers['x-api-key'] = apiKey;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - normalize response and handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Backend returns standardized format: { status, message, data }
    // Return the data portion for convenience
    if (response.data && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  },
  (error) => {
    const errorResponse = handleApiError(error);
    return Promise.reject(errorResponse);
  }
);

/**
 * Handle API errors and return standardized error object
 * @param {Error} error - Axios error object
 * @returns {Object} Standardized error object
 */
export const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;

    switch (status) {
      case 400:
        return {
          type: 'validation',
          status,
          message: data.message || 'Invalid request',
          errors: data.errors || [],
        };
      case 401:
        return {
          type: 'auth',
          status,
          message: 'Invalid or missing API key',
        };
      case 403:
        return {
          type: 'forbidden',
          status,
          message: data.message || 'Access denied',
        };
      case 404:
        return {
          type: 'notFound',
          status,
          message: data.message || 'Resource not found',
        };
      case 422:
        return {
          type: 'validation',
          status,
          message: data.message || 'Validation failed',
          errors: data.errors || [],
        };
      case 429:
        return {
          type: 'rateLimit',
          status,
          message: 'Too many requests. Please try again later.',
        };
      case 500:
      case 502:
      case 503:
        return {
          type: 'server',
          status,
          message: 'Server error. Please try again later.',
        };
      default:
        return {
          type: 'unknown',
          status,
          message: data.message || 'An unexpected error occurred',
        };
    }
  }

  if (error.request) {
    return {
      type: 'network',
      status: 0,
      message: 'Network error. Please check your connection.',
    };
  }

  return {
    type: 'unknown',
    status: 0,
    message: error.message || 'An unexpected error occurred',
  };
};

/**
 * Check if error is an authentication error
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export const isAuthError = (error) => error?.type === 'auth' || error?.status === 401;

/**
 * Check if error is a network error
 * @param {Object} error - Error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => error?.type === 'network';

/**
 * Format error message for display
 * @param {Object|Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const formatErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  return 'An unexpected error occurred';
};

export default apiClient;
